import { useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  MessageSquare,
  Mail,
  MailOpen,
  MailCheck,
  MailX,
  MailWarning,
  Tag as TagIcon,
  Workflow,
  Zap,
  TrendingUp,
  Star,
  Activity,
  Inbox,
  Send,
  ArrowDownLeft,
  ArrowUpRight,
  Search,
  X,
} from "lucide-react";

type TimelineEventType =
  | "whatsapp_sent"
  | "whatsapp_received"
  | "email_sent"
  | "email_delivered"
  | "email_opened"
  | "email_clicked"
  | "email_bounced"
  | "email_unsubscribed"
  | "email_spam"
  | "tag_added"
  | "sequence_enrolled"
  | "flow_started"
  | "score_event"
  | "custom_event"
  | "conversation_event";

const TYPE_META: Record<
  TimelineEventType,
  {
    icon: any;
    tint: string;
    dotColor: string;
    group: "whatsapp" | "email" | "automation" | "system";
    label: string;
  }
> = {
  whatsapp_received: {
    icon: ArrowDownLeft,
    tint: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    dotColor: "#10b981",
    group: "whatsapp",
    label: "WhatsApp",
  },
  whatsapp_sent: {
    icon: ArrowUpRight,
    tint: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
    dotColor: "#0ea5e9",
    group: "whatsapp",
    label: "WhatsApp",
  },
  email_sent: {
    icon: Send,
    tint: "bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300",
    dotColor: "#6366f1",
    group: "email",
    label: "E-mail",
  },
  email_delivered: {
    icon: MailCheck,
    tint: "bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300",
    dotColor: "#64748b",
    group: "email",
    label: "E-mail",
  },
  email_opened: {
    icon: MailOpen,
    tint: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
    dotColor: "#f59e0b",
    group: "email",
    label: "E-mail",
  },
  email_clicked: {
    icon: Zap,
    tint: "bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300",
    dotColor: "#f97316",
    group: "email",
    label: "E-mail",
  },
  email_bounced: {
    icon: MailX,
    tint: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
    dotColor: "#ef4444",
    group: "email",
    label: "E-mail",
  },
  email_unsubscribed: {
    icon: MailWarning,
    tint: "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
    dotColor: "#f43f5e",
    group: "email",
    label: "E-mail",
  },
  email_spam: {
    icon: MailWarning,
    tint: "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
    dotColor: "#f43f5e",
    group: "email",
    label: "E-mail",
  },
  tag_added: {
    icon: TagIcon,
    tint: "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300",
    dotColor: "#8b5cf6",
    group: "system",
    label: "Tag",
  },
  sequence_enrolled: {
    icon: Workflow,
    tint: "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300",
    dotColor: "#a855f7",
    group: "automation",
    label: "Sequência",
  },
  flow_started: {
    icon: Activity,
    tint: "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-950 dark:text-fuchsia-300",
    dotColor: "#d946ef",
    group: "automation",
    label: "Fluxo",
  },
  score_event: {
    icon: TrendingUp,
    tint: "bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300",
    dotColor: "#14b8a6",
    group: "system",
    label: "Pontuação",
  },
  custom_event: {
    icon: Star,
    tint: "bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300",
    dotColor: "#eab308",
    group: "system",
    label: "Evento",
  },
  conversation_event: {
    icon: MessageSquare,
    tint: "bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300",
    dotColor: "#6b7280",
    group: "system",
    label: "Atendimento",
  },
};

type FilterGroup = "all" | "whatsapp" | "email" | "automation" | "system";

const FILTER_LABEL: Record<FilterGroup, string> = {
  all: "Tudo",
  whatsapp: "WhatsApp",
  email: "E-mail",
  automation: "Automação",
  system: "Sistema",
};

function formatDateTime(d: Date | string): string {
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatRelative(d: Date | string): string {
  const date = typeof d === "string" ? new Date(d) : d;
  const diffMs = Date.now() - date.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `${min}min`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h`;
  const days = Math.round(h / 24);
  if (days < 30) return `${days}d`;
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

function dayKey(d: Date | string): string {
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export default function ContactTimeline({
  contactId,
  emptyHint,
}: {
  contactId: number;
  emptyHint?: string;
}) {
  const query = trpc.contacts.timeline.useQuery(
    { contactId, limit: 500 },
    { enabled: !!contactId },
  );
  const [filter, setFilter] = useState<FilterGroup>("all");
  const [search, setSearch] = useState("");

  const events = (query.data ?? []) as Array<{
    id: string;
    type: TimelineEventType;
    occurredAt: Date | string;
    direction?: "in" | "out" | "system";
    channel?: "whatsapp" | "email" | "system";
    title: string;
    subtitle?: string | null;
    content?: string | null;
    metadata?: Record<string, any> | null;
  }>;

  const counts = useMemo(() => {
    const c: Record<FilterGroup, number> = {
      all: events.length,
      whatsapp: 0,
      email: 0,
      automation: 0,
      system: 0,
    };
    for (const e of events) {
      const g = TYPE_META[e.type]?.group;
      if (g) c[g]++;
    }
    return c;
  }, [events]);

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    return events.filter((e) => {
      if (filter !== "all" && TYPE_META[e.type]?.group !== filter) return false;
      if (!term) return true;
      const hay = [e.title, e.subtitle, e.content]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase();
      return hay.includes(term);
    });
  }, [events, filter, search]);

  // Agrupamento por dia
  const groupedByDay = useMemo(() => {
    const groups: Array<{
      key: string;
      events: typeof filtered;
    }> = [];
    let current: { key: string; events: typeof filtered } | null = null;
    for (const e of filtered) {
      const k = dayKey(e.occurredAt);
      if (!current || current.key !== k) {
        current = { key: k, events: [] };
        groups.push(current);
      }
      current.events.push(e);
    }
    return groups;
  }, [filtered]);

  if (query.isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="border rounded-lg py-12 text-center">
        <Inbox className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
        <p className="text-sm font-medium">Sem eventos ainda</p>
        <p className="text-xs text-muted-foreground mt-1">
          {emptyHint ?? "Mensagens, e-mails e automações aparecerão aqui."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Toolbar ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative w-full sm:w-[240px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar na timeline…"
            className="h-8 pl-8 pr-8 text-sm"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 w-5 rounded hover:bg-muted flex items-center justify-center text-muted-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        <div className="inline-flex items-center gap-0.5 p-0.5 bg-muted/60 rounded-md">
          {(Object.keys(FILTER_LABEL) as FilterGroup[]).map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-2 py-1 text-xs font-medium rounded transition inline-flex items-center gap-1 ${
                filter === k
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {FILTER_LABEL[k]}
              <Badge
                variant="secondary"
                className="h-4 px-1 text-[9px] tabular-nums"
              >
                {counts[k]}
              </Badge>
            </button>
          ))}
        </div>
      </div>

      {/* Timeline ────────────────────────────────────────────── */}
      {filtered.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-6">
          Nenhum evento corresponde ao filtro.
        </div>
      ) : (
        <div className="space-y-5">
          {groupedByDay.map((group) => (
            <div key={group.key}>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2 sticky top-0 bg-background/95 backdrop-blur py-1 z-10">
                {group.key}
              </div>
              <div className="relative pl-4 space-y-3 border-l border-border/70">
                {group.events.map((e) => (
                  <TimelineItem key={e.id} event={e} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TimelineItem({
  event,
}: {
  event: {
    id: string;
    type: TimelineEventType;
    occurredAt: Date | string;
    direction?: "in" | "out" | "system";
    channel?: "whatsapp" | "email" | "system";
    title: string;
    subtitle?: string | null;
    content?: string | null;
    metadata?: Record<string, any> | null;
  };
}) {
  const meta = TYPE_META[event.type] ?? TYPE_META.custom_event;
  const Icon = meta.icon;
  return (
    <div className="relative">
      {/* Dot */}
      <span
        className="absolute -left-[21px] top-1 h-3 w-3 rounded-full border-2 border-background"
        style={{ backgroundColor: meta.dotColor }}
      />
      <div className="flex items-start gap-2.5">
        <div
          className={`h-7 w-7 shrink-0 rounded-md flex items-center justify-center ${meta.tint}`}
        >
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="text-sm font-medium truncate">{event.title}</span>
            {event.subtitle && (
              <span className="text-xs text-muted-foreground truncate">
                · {event.subtitle}
              </span>
            )}
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-auto text-[11px] text-muted-foreground tabular-nums shrink-0">
                    {formatRelative(event.occurredAt)}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{formatDateTime(event.occurredAt)}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {event.content && (
            <div className="mt-1 text-xs text-foreground/80 bg-muted/40 rounded px-2 py-1.5 whitespace-pre-wrap break-words">
              {event.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ContactTimelineCompact({
  contactId,
  limit = 8,
}: {
  contactId: number;
  limit?: number;
}) {
  const query = trpc.contacts.timeline.useQuery(
    { contactId, limit: Math.min(limit * 3, 50) },
    { enabled: !!contactId },
  );
  const events = ((query.data ?? []) as any[]).slice(0, limit);
  if (query.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }
  if (events.length === 0) {
    return (
      <div className="text-xs text-muted-foreground text-center py-6">
        Sem eventos ainda.
      </div>
    );
  }
  return (
    <div className="relative pl-4 space-y-2.5 border-l border-border/70">
      {events.map((e: any) => (
        <TimelineItem key={e.id} event={e} />
      ))}
    </div>
  );
}
